import { createContext, useState } from 'react';
import {
  INITIAL_COMPANY_DETAILS,
  INITIAL_ADDRESS_DETAILS,
  INITIAL_ADMIN_DETAILS,
  INITIAL_SIGNATORY_DETAILS,
  INITIAL_FIRM_DETAILS,
  INITIAL_SUPERVISORS_DETAILS,
  INITIAL_INIVITATION_EMAIL_SETTING,
  INITIAL_PROVISION_AGREEMENT,
} from '../constants';
import {
  CollectiveAgreementType,
  CompanyDetails,
  FirmDetails,
  LooseObject,
  AddressDetails,
  AuthorizedSignatory,
  AdminDetails,
  IAccountingFirmContext,
  Consultants,
  Superviors,
  SeniorConsultants,
  IAccountingFirm,
  AccountingFirmFormGroup,
  ProvisionAgreement,
  AuthorizedSignatoryOptionItem,
} from '../types';
import { AuthorizedSignatoryGroup, ResellerAgreementType } from '../../CompanyClients/types';
import { useFormik } from 'formik';
import { formSchema } from './schema';

export const CreateAccountingFirmContext = createContext<IAccountingFirmContext>({
  firmDetails: INITIAL_FIRM_DETAILS,
  setFirmDetails: () => {},

  companyDetails: INITIAL_COMPANY_DETAILS,
  setCompanyDetails: () => {},
  
  authorizedSignatoryOptions: [],
  setAuthorizedSignatoryOptions: () => {},

  signatoryAdmins: [],
  setSignatoryAdmins: () => {},

  deletedSupervisors: [],
  setDeletedSupervisors: () => {},

  deletedConsultants: [],
  setDeletedConsultants: () => {},

  combinations: [],
  setCombinations: () => {},

  addressDetails: INITIAL_ADDRESS_DETAILS,
  setAddressDetails: () => {},

  adminDetails: [],
  setAdminDetails: () => {},

  signatoryDetails: [],
  setSignatoryDetails: () => {},

  contractEffectiveFrom: '',
  setContractEffectiveFrom: () => {},

  invoicingCostCenter: '',
  setInvoicingCostCenter: () => {},

  invoiceEmailAddress: '',
  setInvoiceEmailAddress: () => {},

  consultants: [],
  setConsultants: () => [],

  supervisors: [],
  setSupervisors: () => [],

  seniorConsultants: [],
  setSeniorConsultants: () => [],

  invitationEmail: INITIAL_INIVITATION_EMAIL_SETTING,
  setInvitationEmail: () => {},

  collectiveAgreements: [],
  setCollectiveAgreements: () => {},

  resellerAgreements: [],
  setResellerAgreements: () => {},

  provisionAgreement: INITIAL_PROVISION_AGREEMENT,
  setProvisionAgreement: () => {},

  isEditMode: false,
  formGroup: {} as AccountingFirmFormGroup,

  updateFirmDetailsInput: () => {},
  updateAddressInput: () => {},
  updateContractEffectiveInput: () => {},
  updateInvoicingCostCenter: () => {},
  updateInvoiceEmailAddress: () => {},
  replaceSupervisorsInput: () => {},
  replaceSignatoriesInput: () => {},
  replaceSeniorConsultantsInput: () => {},
  updateInvitationEmailInput: () => {},
  replaceFirmDetailsInput: () => {},
  replaceAddressInput: () => {},
  updateProvisionAgreementInput: () => {},
  replaceSignatoryAdmins: () => {},
  appendDeletedSupervisor: () => {},
  appendDeletedConsultant: () => {},
  replaceCombinations: () => {},
  updateCombinations: () => {},
  replaceProvisionAgreementInput: () => {},
});

type Props = {
  children: JSX.Element;
};

export const INITIAL_ACCOUNTING_FIRM = {
  firmDetails: {
    ...INITIAL_FIRM_DETAILS,
  },
  addressDetails: INITIAL_ADDRESS_DETAILS,
  contractEffectiveFrom: '',
  invoicingCostCenter: '',
  invoiceEmailAddress: '',
  supervisors: [{ _id: `${Math.random()}`, ...INITIAL_SUPERVISORS_DETAILS }],
  seniorConsultants: [],
  signatoryDetails: [
    { _id: `${Math.random()}`, ...INITIAL_SIGNATORY_DETAILS, incharge: true },
    { _id: `${Math.random()}`, ...INITIAL_SIGNATORY_DETAILS },
  ],
  combinations: [],
  invitationEmail: INITIAL_INIVITATION_EMAIL_SETTING,
  provisionAgreement: INITIAL_PROVISION_AGREEMENT,
} as IAccountingFirm;

export const CreateAccountingFirmProvider = ({ children }: Props) => {
  const formGroup = useFormik({
    initialValues: INITIAL_ACCOUNTING_FIRM,
    validateOnMount: true,
    validationSchema: formSchema,
    onSubmit: () => {},
  });

  const [firmDetails, setFirmDetails] = useState<FirmDetails>(INITIAL_FIRM_DETAILS);
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails | LooseObject>(INITIAL_COMPANY_DETAILS);
  const [addressDetails, setAddressDetails] = useState<AddressDetails | LooseObject>(INITIAL_ADDRESS_DETAILS);
  const [adminDetails, setAdminDetails] = useState<Array<AdminDetails>>([
    { _id: `${Math.random()}`, ...INITIAL_ADMIN_DETAILS },
  ]);
  const [signatoryDetails, setSignatoryDetails] = useState<Array<AuthorizedSignatory>>([
    { _id: `${Math.random()}`, ...INITIAL_SIGNATORY_DETAILS, incharge: true },
    { _id: `${Math.random()}`, ...INITIAL_SIGNATORY_DETAILS },
  ]);
  const [consultants, setConsultants] = useState<Array<Consultants>>([]);
  const [supervisors, setSupervisors] = useState<Array<Superviors>>([
    { _id: `${Math.random()}`, ...INITIAL_SUPERVISORS_DETAILS },
  ]);
  const [seniorConsultants, setSeniorConsultants] = useState<Array<SeniorConsultants>>([]);
  const [contractEffectiveFrom, setContractEffectiveFrom] = useState<string>('');

  const [invoicingCostCenter, setInvoicingCostCenter] = useState<string>('');
  const [invoiceEmailAddress, setInvoiceEmailAddress] = useState<string>('');
  const [collectiveAgreements, setCollectiveAgreements] = useState<Array<CollectiveAgreementType>>([]);
  const [resellerAgreements, setResellerAgreements] = useState<Array<ResellerAgreementType>>([]);
  const [invitationEmail, setInvitationEmail] = useState<string>(INITIAL_INIVITATION_EMAIL_SETTING);
  const [authorizedSignatoryOptions, setAuthorizedSignatoryOptions] = useState<Array<AuthorizedSignatoryOptionItem[]>>([]);
  const [provisionAgreement, setProvisionAgreement] = useState<ProvisionAgreement>(INITIAL_PROVISION_AGREEMENT);
  const [signatoryAdmins, setSignatoryAdmins] = useState<Array<AuthorizedSignatory>>([]);
  const [deletedSupervisors, setDeletedSupervisors] = useState<Array<string>>([]);
  const [deletedConsultants, setDeletedConsultants] = useState<Array<string>>([]);
  const [combinations, setCombinations] = useState<Array<AuthorizedSignatoryGroup>>([]);
  const isEditMode = false;

  const replaceSignatoryAdmins = (value) => {
    setSignatoryAdmins(value);
    formGroup.setFieldValue('signatoryAdmins', value);
  };

  const replaceCombinations = (value) => {
    setCombinations(value);
    formGroup.setFieldValue('combinations', value);
  };

  const updateCombinations = (inputName, inputValue) => {
    setCombinations((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`combinations.${inputName}`, inputValue);
  };

  const appendDeletedSupervisor = (value) => {
    if (!isEditMode) {
      return;
    }

    const _deletedSupervisors = [...deletedSupervisors];
    _deletedSupervisors.push(value);

    setDeletedSupervisors(_deletedSupervisors);
  };

  const appendDeletedConsultant = (value) => {
    if (!isEditMode) {
      return;
    }

    const _deletedConsultants = [...deletedConsultants];
    _deletedConsultants.push(value);

    setDeletedConsultants(_deletedConsultants);
  };

  const updateFirmDetailsInput = (inputName, inputValue) => {
    setFirmDetails((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`firmDetails.${inputName}`, inputValue);
  };

  const updateAddressInput = (inputName, inputValue) => {
    setAddressDetails((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`addressDetails.${inputName}`, inputValue);
  };

  const replaceFirmDetailsInput = (inputValue) => {
    setFirmDetails(inputValue);
    formGroup.setFieldValue('firmDetails', inputValue);
    formGroup.setFieldTouched('firmDetails');
  };

  const replaceAddressInput = (inputValue) => {
    setAddressDetails(inputValue);
    formGroup.setFieldValue('addressDetails', inputValue);
    formGroup.setFieldTouched('addressDetails');
  };

  const updateContractEffectiveInput = (inputValue) => {
    setContractEffectiveFrom(inputValue);
    formGroup.setFieldValue(`contractEffectiveFrom`, inputValue);
  };

  const updateInvoicingCostCenter = (inputValue) => {
    setInvoicingCostCenter(inputValue);
    formGroup.setFieldValue(`invoicingCostCenter`, inputValue);
  };

  const updateInvoiceEmailAddress = (inputValue) => {
    setInvoiceEmailAddress(inputValue);
    formGroup.setFieldValue(`invoiceEmailAddress`, inputValue);
  };

  const replaceSupervisorsInput = (inputValue) => {
    setSupervisors(inputValue);
    formGroup.setFieldValue('supervisors', inputValue);
  };

  const replaceSeniorConsultantsInput = (inputValue) => {
    setSeniorConsultants(inputValue);
    formGroup.setFieldValue('seniorConsultants', inputValue);
  };

  const replaceSignatoriesInput = (inputValue) => {
    setSignatoryDetails(inputValue);
    formGroup.setFieldValue('signatoryDetails', inputValue);
  };

  const updateInvitationEmailInput = (inputValue) => {
    setInvitationEmail(inputValue);
    formGroup.setFieldValue(`invitationEmail`, inputValue);
  };
  const updateProvisionAgreementInput = (inputValue) => {
    setProvisionAgreement(inputValue);
    formGroup.setFieldValue(`provisionAgreement`, inputValue);
  };

  const replaceProvisionAgreementInput = (inputValue) => {
    setProvisionAgreement(inputValue);
    formGroup.setFieldValue(`provisionAgreement`, inputValue);
  };

  const context: IAccountingFirmContext = {
    firmDetails,
    setFirmDetails,
    companyDetails,
    setCompanyDetails,
    authorizedSignatoryOptions,
    setAuthorizedSignatoryOptions,
    adminDetails,
    setAdminDetails,
    signatoryDetails,
    setSignatoryDetails,
    addressDetails,
    setAddressDetails,
    consultants,
    setConsultants,
    seniorConsultants,
    setSeniorConsultants,
    supervisors,
    setSupervisors,
    invitationEmail,
    setInvitationEmail,
    contractEffectiveFrom,
    setContractEffectiveFrom,
    isEditMode: false,
    collectiveAgreements,
    setCollectiveAgreements,
    resellerAgreements,
    setResellerAgreements,
    invoicingCostCenter,
    setInvoicingCostCenter,
    invoiceEmailAddress,
    setInvoiceEmailAddress,
    provisionAgreement,
    setProvisionAgreement,
    formGroup,
    signatoryAdmins,
    setSignatoryAdmins,
    deletedSupervisors,
    setDeletedSupervisors,
    deletedConsultants,
    setDeletedConsultants,
    combinations,
    setCombinations,
    replaceSignatoryAdmins,
    replaceCombinations,
    updateCombinations,
    appendDeletedSupervisor,
    appendDeletedConsultant,
    updateFirmDetailsInput,
    updateAddressInput,
    updateContractEffectiveInput,
    updateInvoicingCostCenter,
    updateInvoiceEmailAddress,
    updateProvisionAgreementInput,
    replaceSupervisorsInput,
    replaceSignatoriesInput,
    replaceSeniorConsultantsInput,
    updateInvitationEmailInput,
    replaceFirmDetailsInput,
    replaceAddressInput,
    replaceProvisionAgreementInput,
  };

  return <CreateAccountingFirmContext.Provider value={context}>{children}</CreateAccountingFirmContext.Provider>;
};
